import { useNavigation } from '@react-navigation/native';
import { useFormikContext } from 'formik';
import React, {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, Platform, StyleSheet, TouchableOpacity } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';

import Avatar from '@components/Avatar/Avatar';
import { AddMembersSection } from '@components/Common/AddMembersSection';
import { TeamPaywallModal } from '@components/Modals/TeamPaywallModal';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import { useProjectTutorial } from '@hooks/useProjectTutorial';
import useSelectedMembers from '@hooks/useSelectedMembers';
import useTaskCreateFooter from '@hooks/useTaskCreateFooter';
import { useTeamBillingFromQuery } from '@hooks/useTeamBillingFromQuery';
import theme from '@themes/theme';
import { shouldShowPaywall } from '@utils/teams/paywall';

import { FormValues } from '../Projects/ProjectFormStack';

type ProjectCreateFormModalProps = {
  pickFiles: () => void;
  pickImages: () => void;
  launchCamera: () => void;
  onSave?: () => void;
  dismissProjectModal: () => void;
  onFormValidityChange?: (isFormBlank: boolean) => void;
  setNoNeedShowDiscard: React.Dispatch<React.SetStateAction<boolean>>;
  handleTeamSelectorPress: () => void;
} & ComponentProps<typeof AddMembersSection>;

export const ProjectCreateModalFooter: React.FC<
  ProjectCreateFormModalProps
> = ({
  handleTeamSelectorPress,
  pickFiles,
  pickImages,
  launchCamera,
  onSave,
  onFormValidityChange,
  dismissProjectModal,
  setNoNeedShowDiscard,
}) => {
  const { isReadySubmitting } = useTaskCreateFooter();
  const { selectedMembers } = useSelectedMembers();
  const { t } = useTranslation();
  const {
    isVisible,
    projectTutorialState,
    setProjectTutorialStateAndInStorage,
  } = useProjectTutorial();

  const { values, errors, isSubmitting, isValid, setFieldValue } =
    useFormikContext<FormValues>();

  //   const { projectId, projectUUId, name } = values;

  const { teamId = '', teamUUId = '' } = values;

  const { teams } = useListTeamsFromQuery();

  const primaryTeam = teams[0];

  useEffect(() => {
    if (!values.teamId && !values.teamUUId && primaryTeam?.id) {
      setFieldValue('teamId', primaryTeam.id);
    }
  }, [primaryTeam, values.teamId, values.teamUUId, setFieldValue]);

  const selectedTeam = useMemo(() => {
    if (teamId) return teams.find((item) => item.id === teamId);
    if (teamUUId) return teams.find((item) => item.uuid === teamUUId);
    return undefined;
  }, [teams, teamId, teamUUId]);
  // TA-9872: subscriptionDetails was split out of listTeams — merge it back
  // from the getTeamBilling query for the paywall check.
  const { billingByTeamId, error: billingError } = useTeamBillingFromQuery();
  const selectedTeamWithBilling = selectedTeam && {
    ...selectedTeam,
    subscriptionDetails: billingByTeamId[selectedTeam.id]?.subscriptionDetails,
  };
  const areAllFieldsBlank = () => {
    const { name, description, files } = values;

    return (
      !name?.trim() &&
      !description?.trim() &&
      files?.length === 0 &&
      selectedMembers?.length === 0
    );
  };

  const submitForm = () => {
    onSave?.();
  };

  const saveProjectTutorializationState = async (currentState: string) => {
    setProjectTutorialStateAndInStorage(currentState);
  };

  useEffect(() => {
    const isFormBlank = areAllFieldsBlank();
    onFormValidityChange?.(isFormBlank);
  }, [values, errors]);

  const { description } = values;

  useEffect(() => {
    if (projectTutorialState && isVisible) {
      if (description && description?.length > 0) {
        if (Number(projectTutorialState) > 3) {
          saveProjectTutorializationState(projectTutorialState?.toString());
          return;
        }
      }
    }
  }, [description?.length, projectTutorialState, isVisible]);

  const [memberCount, setMemberCount] = useState(0);

  useEffect(() => {
    setMemberCount(selectedMembers?.length || 0);
    if (
      (selectedMembers?.length || memberCount) &&
      projectTutorialState === '4' &&
      isVisible
    ) {
      setProjectTutorialStateAndInStorage('5');
    }
  }, [selectedMembers, memberCount, projectTutorialState, isVisible]);
  const _projectLeft =
    (selectedTeam?.maxNumberOfProjects ?? 0) -
    (selectedTeam?.numberOfProjectsUsed ?? 0);

  useEffect(() => {
    if (selectedTeam?.id) {
      setFieldValue('teamId', selectedTeam?.id);
      setFieldValue('teamUUId', selectedTeam?.uuid);
      setFieldValue('currentTeam', selectedTeam);
    }
  }, [selectedTeam?.id]);

  const onPressAddMembers = () => {
    if (isReadySubmitting) return;
    setNoNeedShowDiscard(true);
    Keyboard.dismiss();
    navigation.navigate('project-invite-or-add-member', {
      projectId: values.projectId,
      isCreateProject: true,
      selectedTeamId: teamId,
    });
  };

  const onLocationPress = () => {
    (navigation as any).navigate('app-map-location-search', {
      onSelect: (location: Location) => {
        setFieldValue('location', location);
      },
    });
  };

  const handleSkipPress = useCallback(() => {
    if (!teamId) {
      setFieldValue('teamId', primaryTeam?.id);
    }
    saveProjectTutorializationState('6');
    dismissProjectModal();
  }, [
    teamId,
    primaryTeam?.id,
    setFieldValue,
    saveProjectTutorializationState,
    dismissProjectModal,
  ]);

  // Align with web: use shouldShowPaywall from paywall util (handles personal at limit
  // and non-personal expired; trial-eligible users see paywall modal to start trial)
  const shouldShowPaywallFlag = shouldShowPaywall(
    selectedTeamWithBilling ?? null
  );

  // Billing loads asynchronously (TA-9872). Until it has resolved for the
  // selected team, shouldShowPaywall would read subscriptionDetails as
  // undefined (== not expired) and could let an expired team through. Hold
  // submit until billing is present for the selected team. If billing errored,
  // don't block creation — fall back to backend enforcement of the paywall.
  const billingReadyForSelected =
    !selectedTeam ||
    !!billingError ||
    billingByTeamId[selectedTeam.id] !== undefined;

  const shouldProjectCreateDisabled = shouldShowPaywallFlag;

  const [showPaywall, setShowPaywall] = useState(false);

  const disabled = !values?.name?.trim() || !teamId || isSubmitting;

  const navigation = useNavigation();

  // Compute disabled state internally using isValid and isSubmitting from Formik context
  // This avoids needing these values passed as props, which would cause parent callback recreation
  const isDisabled =
    !isValid ||
    isSubmitting ||
    !values?.name?.trim() ||
    !values?.teamId ||
    !billingReadyForSelected ||
    disabled;

  return (
    <Box flex={1}>
      <KeyboardAwareScrollView
        keyboardShouldPersistTaps='handled'
        showsVerticalScrollIndicator={false}
        style={{ flex: 1 }}>
        <Box flex={1}>
          {!values.isOnboardingProject && (
            <Box
              flexDirection='row'
              borderColor='grey02'
              borderTopWidth={1}
              borderBottomWidth={1}
              paddingVertical='s'
              paddingHorizontal='xs'>
              <Box
                flex={1}
                style={{
                  alignItems: 'center',
                  paddingHorizontal: theme.spacing.xs,
                  paddingVertical: theme.spacing.s,
                }}>
                <TouchableOpacity
                  disabled={isReadySubmitting}
                  accessibilityLabel='Add File'
                  onPress={pickFiles}>
                  <Icon
                    name='Paperclip'
                    color={isReadySubmitting ? 'grey04' : 'grey05'}
                  />
                </TouchableOpacity>
              </Box>
              <Box
                flex={1}
                style={{
                  alignItems: 'center',
                  paddingHorizontal: theme.spacing.xs,
                  paddingVertical: theme.spacing.s,
                }}>
                <TouchableOpacity
                  disabled={isReadySubmitting}
                  accessibilityLabel='Image - Attach library images'
                  onPress={pickImages}>
                  <Icon
                    name='Image'
                    color={isReadySubmitting ? 'grey04' : 'grey05'}
                  />
                </TouchableOpacity>
              </Box>
              <Box
                flex={1}
                style={{
                  alignItems: 'center',
                  paddingHorizontal: theme.spacing.xs,
                  paddingVertical: theme.spacing.s,
                }}>
                <TouchableOpacity
                  disabled={isReadySubmitting}
                  accessibilityLabel='Camera - Attach camera images'
                  onPress={launchCamera}>
                  <Icon
                    name='Camera'
                    color={isReadySubmitting ? 'grey04' : 'grey05'}
                  />
                </TouchableOpacity>
              </Box>
              <Box
                flex={1}
                style={{
                  alignItems: 'center',
                  paddingHorizontal: theme.spacing.xs,
                  paddingVertical: theme.spacing.s,
                }}>
                <TouchableOpacity
                  disabled={isReadySubmitting}
                  // accessibilityLabel={t('models:tasks.create.dates.dueDate')}
                  onPress={onLocationPress}>
                  <Icon
                    name='MapPin'
                    color={isReadySubmitting ? 'grey04' : 'grey05'}
                  />
                </TouchableOpacity>
              </Box>
              <Box
                borderColor='grey02'
                borderLeftWidth={1}
                marginHorizontal='xs'
              />
              <Box
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 100,
                  backgroundColor:
                    memberCount === 0 ? theme.colors.grey01 : undefined,
                  paddingHorizontal:
                    memberCount === 0 ? theme.spacing.xs : undefined,
                  paddingVertical:
                    memberCount === 0 ? theme.spacing.s : undefined,
                  marginHorizontal: theme.spacing.xs,
                  width: memberCount === 0 ? 40 : undefined,
                  height: memberCount === 0 ? 40 : undefined,
                }}>
                <TouchableOpacity
                  disabled={isReadySubmitting}
                  accessibilityLabel={t('models:tasks.create.members.title')}
                  onPress={onPressAddMembers}>
                  {memberCount === 0 ? (
                    <Icon name='UserPlus' color='grey05' />
                  ) : (
                    <Avatar
                      id={selectedMembers?.[0]?.id}
                      avatar={selectedMembers?.[0]?.avatar}
                      size='medium'
                      label={selectedMembers?.[0]?.name}
                      onPress={onPressAddMembers}
                    />
                  )}
                </TouchableOpacity>
                {isReadySubmitting && (
                  <Box
                    borderRadius='xl'
                    backgroundColor='grey04'
                    opacity={0.5}
                    alignItems='center'
                    justifyContent='center'
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 2,
                      width: 40,
                      height: 40,
                    }}></Box>
                )}
                {memberCount > 0 && (
                  <Box
                    borderRadius='xl'
                    backgroundColor={isReadySubmitting ? 'grey04' : 'black'}
                    alignItems='center'
                    justifyContent='center'
                    style={{
                      position: 'absolute',
                      right: -2,
                      top: -2,
                      width: 16,
                    }}>
                    <Text variant='labelRequired' color='white'>
                      {memberCount}
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {values.isOnboardingProject && (
            // The CTA row above (hidden during onboarding, #1) carried its
            // own borderTopWidth/borderBottomWidth — hiding the whole row
            // silently dropped the separator line above this footer too.
            // Render just that line on its own so onboarding still gets the
            // same visual separation as the standard flow (dev review,
            // PR #5939).
            <Box
              testID='onboarding-footer-separator'
              borderColor='grey02'
              borderTopWidth={1}
            />
          )}

          <Box
            flex={1}
            paddingHorizontal='m'
            paddingVertical='s'
            flexDirection='row'
            alignItems='center'>
            <Box flex={1}>
              <TouchableOpacity
                onPress={handleTeamSelectorPress}
                style={styles.teamSelectorButton}
                disabled={isReadySubmitting}>
                <Box flexDirection='row' alignItems='center' flex={1}>
                  {selectedTeam?.avatarFile ? (
                    // @ts-ignore - Avatar component has incorrect return type in type definition
                    <Avatar
                      key={selectedTeam.id}
                      size='xs'
                      disableAvatarPress
                      avatar={{
                        id: selectedTeam.avatarFile.id,
                        cdnBaseUrl: selectedTeam.avatarFile.cdnBaseUrl,
                        isPreviewable: selectedTeam.avatarFile.isPreviewable,
                        path: selectedTeam.avatarFile.path,
                        url: selectedTeam.avatarFile.url,
                      }}
                      id={selectedTeam.id}
                      avatarType='team'
                      marginLeft={theme.spacing.xs}
                    />
                  ) : (
                    <Icon name='TTProject' color='textPrimary' variant='m' />
                  )}

                  <Text
                    variant='typography'
                    color={isReadySubmitting ? 'grey04' : 'black'}
                    marginLeft='s'
                    numberOfLines={1}
                    style={{ marginRight: theme.spacing.m }}>
                    {selectedTeam?.name}
                  </Text>
                  <Icon
                    name='ChevronDown'
                    color={isReadySubmitting ? 'grey04' : 'textPrimary'}
                    variant='s'
                  />
                </Box>
              </TouchableOpacity>
            </Box>
            <Box>
              {isVisible &&
              projectTutorialState === '5' &&
              (disabled || shouldProjectCreateDisabled) ? (
                <TouchableOpacity
                  onPress={handleSkipPress}
                  disabled={false}
                  accessibilityLabel={t('shared:save')}>
                  <Box
                    alignItems='center'
                    justifyContent='center'
                    borderRadius='xxl'
                    bg='greenSecondary'
                    width={80}
                    height={40}>
                    <Text variant='labelEmphasized' color='white'>
                      Skip
                    </Text>
                  </Box>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  onPress={() => {
                    // Check paywall on mobile before submitting
                    if (Platform.OS !== 'web' && shouldShowPaywallFlag) {
                      setShowPaywall(true);
                      return;
                    }
                    if (isVisible) {
                      setNoNeedShowDiscard(true);
                      setProjectTutorialStateAndInStorage('6');
                    }
                    submitForm();
                  }}
                  disabled={isDisabled || isReadySubmitting}
                  accessibilityLabel={t('shared:save')}>
                  <Box
                    alignItems='center'
                    justifyContent='center'
                    borderRadius='xxl'
                    bg={
                      isSubmitting || isDisabled || isReadySubmitting
                        ? 'grey04'
                        : 'greenSecondary'
                    }
                    width={40}
                    height={40}>
                    <Icon variant='xl' name='ArrowUp' color='white' />
                  </Box>
                </TouchableOpacity>
              )}
            </Box>
          </Box>
        </Box>
      </KeyboardAwareScrollView>
      {Platform.OS !== 'web' && (
        <TeamPaywallModal
          visible={showPaywall}
          onClose={() => setShowPaywall(false)}
          team={selectedTeam ?? undefined}
        />
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  teamSelectorButton: {
    flex: 1,
    marginRight: theme.spacing.m,
    paddingVertical: theme.spacing.s,
    backgroundColor: theme.colors.white,
  },
});
